import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Plus } from "lucide-react";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { ProcessMessagesButton } from "components/ProcessMessagesButton/ProcessMessagesButton";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Switch } from "components/ui/Switch";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { HealthlinkMisconfiguredNotice } from "features/healthlink/components/HealthlinkMisconfiguredNotice";
import { useHealthlinkPracticeStatus } from "features/healthlink/hooks/useHealthlinkPracticeStatus";

import { AddVaccineForm } from "../../patient/PatientImmunizationsPage/components/AddVaccineForm/AddVaccineForm";
import { VACCINE_MODE } from "../../patient/PatientImmunizationsPage/components/AddVaccineForm/components/VaccineModeSwitch";
import {
  MANUFACTURER_OPTIONS,
  VACCINE_TYPES,
} from "../../patient/PatientImmunizationsPage/components/AddVaccineForm/constants";
import { AllImmunisationsTable } from "./components/AllImmunisationsTable/AllImmunisationsTable";
import { DeclinedVaccinesTable } from "./components/DeclinedVaccinesTable";
import { HealthlinkImmunisationsTable } from "./components/HealthlinkImmunisationsTable/HealthlinkImmunisationsTable";
import { getTabOptions } from "./components/ImmunisationsNavbar/utils/menuItems";
import { selectedItemToTitle } from "./components/ImmunisationsNavbar/utils/selectedItemToTitle";
import { VaccineRecallTable } from "./components/VaccineRecallTable";
import styles from "./ImmunizationsPage.module.css";

const DECLINED_MONTHS_BACK = 3;

const toIsoDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDefaultDeclinedRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - DECLINED_MONTHS_BACK);

  return { from: toIsoDate(from), to: toIsoDate(to) };
};

const ImmunizationsPage = () => {
  const [selectedItem, setSelectedItem] = useState("healthlink");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    dateGivenFrom: "",
    dateGivenTo: "",
    vaccineType: "",
    batchNo: "",
    vaccineManufacturer: "",
    vaccineName: "",
  });

  const tenantVal = localStorage.getItem("kinesin-tenant");

  // HealthLink features are only usable when a practice certificate is
  // configured. If HealthLink is enabled in settings but not configured, we
  // hide the HealthLink tab and prompt for configuration instead.
  const { isConfigured, isMisconfigured } = useHealthlinkPracticeStatus();

  useEffect(() => {
    if (!isConfigured && selectedItem === "healthlink") {
      setSelectedItem("manual");
    }
  }, [isConfigured, selectedItem]);

  const handleFilterChange = (field, value) => {
    setAdvancedFilters((prev) => ({ ...prev, [field]: value }));
  };

  const isDeclinedView = selectedItem === "declined";
  const isRecallView = selectedItem === "recall";

  // The declined list is filtered by when the decision was made, not by when a dose was
  // given, so it needs its own range — but it lives in the same filters card as every
  // other tab's, because a filter that moves about between tabs reads as a different
  // control each time.
  const [declinedRange, setDeclinedRange] = useState(getDefaultDeclinedRange);

  const handleDeclinedRangeChange = (field) => (date) =>
    setDeclinedRange((previous) => ({ ...previous, [field]: toIsoDate(date) }));

  // Recall's two controls live in the same card for the same reason. An empty due-by
  // means due or overdue today, which is the list a practice opens this for.
  const [recallDueBy, setRecallDueBy] = useState("");
  const [includeUncertainHistory, setIncludeUncertainHistory] = useState(false);
  /*
   * Defaults to showing everyone, contacted families included, matching the backend
   * (KIN-1890). Hiding them would understate the work outstanding, and a practice manager
   * opening this list should see the whole picture. Turning it off is what makes the list
   * workable a second time in one day.
   */
  const [includeNotified, setIncludeNotified] = useState(true);

  // Neither of those two tabs lists administered doses — one is what was not
  // given, the other is what is still outstanding. The dose filters and Add
  // vaccine describe an administered vaccination, so they belong to neither.
  const isDoseListView = !isDeclinedView && !isRecallView;

  const tabOptions = useMemo(
    () =>
      getTabOptions().filter(
        (tab) => isConfigured || tab.value !== "healthlink"
      ),
    [isConfigured]
  );

  return (
    <div className={styles.pageContainer}>
      {tabOptions.length > 1 && (
        <TabSwitcher
          options={tabOptions}
          value={selectedItem}
          onChange={setSelectedItem}
          fullWidth
          marginTop={16}
          marginBottom={24}
        />
      )}

      {/* Same card, same grid, same calendar control as the dose tabs — only the two
          fields differ, because this list is filtered by when the decision was made
          rather than by when a dose was given. */}
      {!!isRecallView && (
        <div className={styles.contentWrapper} style={{ marginBottom: "24px" }}>
          <div className={styles.filtersGrid}>
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Due By</label>
              <CustomCalendar
                value={recallDueBy || null}
                onChange={(date) => setRecallDueBy(toIsoDate(date))}
                placeholder="Today"
              />
            </div>
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Incomplete History</label>
              <div className={styles.filterSwitch}>
                <Switch
                  id="include-uncertain-history"
                  checked={includeUncertainHistory}
                  onChange={setIncludeUncertainHistory}
                  label="Include these patients"
                />
              </div>
            </div>
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Already Contacted</label>
              <div className={styles.filterSwitch}>
                <Switch
                  id="include-notified"
                  checked={includeNotified}
                  onChange={setIncludeNotified}
                  label="Show families already contacted"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {!!isDeclinedView && (
        <div className={styles.contentWrapper} style={{ marginBottom: "24px" }}>
          <div className={styles.filtersGrid}>
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Declined After</label>
              <CustomCalendar
                value={declinedRange.from || null}
                onChange={handleDeclinedRangeChange("from")}
                placeholder="Start date"
              />
            </div>
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Declined Before</label>
              <CustomCalendar
                value={declinedRange.to || null}
                onChange={handleDeclinedRangeChange("to")}
                placeholder="End date"
              />
            </div>
          </div>
        </div>
      )}

      {!!isDoseListView && (
      <div className={styles.contentWrapper} style={{ marginBottom: "24px" }}>
        <div className={styles.filtersGrid}>
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Given After</label>
            <CustomCalendar
              value={advancedFilters.dateGivenFrom || null}
              onChange={(date) => {
                if (date) {
                  const y = date.getFullYear();
                  const m = String(date.getMonth() + 1).padStart(2, "0");
                  const d = String(date.getDate()).padStart(2, "0");
                  handleFilterChange("dateGivenFrom", `${y}-${m}-${d}`);
                } else {
                  handleFilterChange("dateGivenFrom", "");
                }
              }}
              placeholder="Start date"
            />
          </div>
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Given Before</label>
            <CustomCalendar
              value={advancedFilters.dateGivenTo || null}
              onChange={(date) => {
                if (date) {
                  const y = date.getFullYear();
                  const m = String(date.getMonth() + 1).padStart(2, "0");
                  const d = String(date.getDate()).padStart(2, "0");
                  handleFilterChange("dateGivenTo", `${y}-${m}-${d}`);
                } else {
                  handleFilterChange("dateGivenTo", "");
                }
              }}
              placeholder="End date"
            />
          </div>
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Vaccine</label>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Search vaccine..."
              value={advancedFilters.vaccineName}
              onChange={(e) =>
                handleFilterChange("vaccineName", e.target.value)
              }
            />
          </div>
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Batch No</label>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Search batch..."
              value={advancedFilters.batchNo}
              onChange={(e) => handleFilterChange("batchNo", e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Manufacturer</label>
            {selectedItem === "healthlink" ? (
              <Autocomplete
                options={MANUFACTURER_OPTIONS}
                getOptionLabel={(option) => option.label}
                value={
                  MANUFACTURER_OPTIONS.find(
                    (m) => m.value === advancedFilters.vaccineManufacturer
                  ) || null
                }
                onChange={(_e, newValue) =>
                  handleFilterChange(
                    "vaccineManufacturer",
                    newValue?.value || ""
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="All" />
                )}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    borderRadius: "12px",
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: "14px",
                    backgroundColor: "#fdfdfd",
                    "& fieldset": { borderColor: "#8f8fa1" },
                    "&:hover fieldset": { borderColor: "#8f8fa1" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#006546",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiAutocomplete-input": {
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: "14px",
                    padding: "0 16px !important",
                  },
                  "& .MuiAutocomplete-input::placeholder": {
                    color: "#8f8fa1",
                    opacity: 1,
                  },
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "12px",
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: "14px",
                      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.15)",
                    },
                  },
                }}
              />
            ) : (
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Search manufacturer..."
                value={advancedFilters.vaccineManufacturer}
                onChange={(e) =>
                  handleFilterChange("vaccineManufacturer", e.target.value)
                }
              />
            )}
          </div>
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Type</label>
            <select
              className={styles.filterInput}
              value={advancedFilters.vaccineType}
              onChange={(e) =>
                handleFilterChange("vaccineType", e.target.value)
              }
            >
              <option value="">All</option>
              {VACCINE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {isMisconfigured && <HealthlinkMisconfiguredNotice />}

      <div className={styles.contentWrapper}>
        <div className={styles.contentHeader}>
          <h3>{selectedItemToTitle(selectedItem)}</h3>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            {selectedItem === "healthlink" && (
              <ProcessMessagesButton
                title="Process vaccine responses"
                successMessage="Vaccine responses processed successfully!"
              />
            )}
            {!!isDoseListView && (
              <Button
                variant={selectedItem === "healthlink" ? "primary" : "text"}
                onClick={() => setIsModalOpen(true)}
                endIcon={Plus}
              >
                Add vaccine
              </Button>
            )}
          </div>
        </div>

        {!!isDeclinedView && <DeclinedVaccinesTable range={declinedRange} />}
        {!!isRecallView && (
          <VaccineRecallTable
            dueBy={recallDueBy}
            includeUncertainHistory={includeUncertainHistory}
            includeNotified={includeNotified}
          />
        )}
        {isDoseListView &&
          (isConfigured && selectedItem === "healthlink" ? (
            <HealthlinkImmunisationsTable
              tenantVal={tenantVal}
              advancedFilters={advancedFilters}
            />
          ) : (
            <AllImmunisationsTable
              tenantVal={tenantVal}
              advancedFilters={advancedFilters}
            />
          ))}
      </div>

      <Dialog
        open={isModalOpen}
        title="Add new vaccine"
        onClose={() => setIsModalOpen(false)}
        width={1310}
      >
        <AddVaccineForm
          closeModal={() => setIsModalOpen(false)}
          tenantVal={tenantVal}
          showPatientSelect={true}
          healthlinkEnabled={isConfigured}
          defaultMode={
            selectedItem === "healthlink"
              ? VACCINE_MODE.HEALTHLINK
              : VACCINE_MODE.MANUAL
          }
        />
      </Dialog>
    </div>
  );
};

export { ImmunizationsPage };
